import { useState, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Mail, Users, Send, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATES = {
  fr: {
    label: 'Français',
    custom: {
      label: 'Message libre',
      subject: '',
      message: '',
    },
    bugfix: {
      label: 'Correction de bug',
      subject: 'ProdPilot — Correction d\'un problème technique',
      message:
        "Bonjour,\n\nNous avons identifié et corrigé un problème technique sur la plateforme ProdPilot. " +
        "Tout est désormais rentré dans l'ordre et vous pouvez reprendre votre activité normalement.\n\n" +
        "Si vous rencontrez encore une difficulté, n'hésitez pas à nous écrire en réponse à cet email.\n\n" +
        "Merci pour votre patience,\nL'équipe ProdLink",
    },
    update: {
      label: 'Mise à jour de la plateforme',
      subject: 'ProdPilot — Nouvelle mise à jour disponible',
      message:
        "Bonjour,\n\nUne nouvelle version de la plateforme ProdPilot vient d'être déployée. " +
        "Elle apporte plusieurs améliorations destinées à fluidifier votre quotidien sur l'outil.\n\n" +
        "Connectez-vous dès maintenant pour découvrir les nouveautés.\n\n" +
        "L'équipe ProdLink",
    },
    announce: {
      label: 'Annonce importante',
      subject: 'ProdPilot — Annonce importante',
      message:
        "Bonjour,\n\nNous souhaitons vous informer d'une actualité importante concernant votre collaboration " +
        "avec ProdLink.\n\n[Précisez ici votre annonce]\n\nL'équipe ProdLink",
    },
  },
  en: {
    label: 'English',
    custom: {
      label: 'Free message',
      subject: '',
      message: '',
    },
    bugfix: {
      label: 'Bug fix',
      subject: 'ProdPilot — Technical issue fixed',
      message:
        "Hello,\n\nWe have identified and fixed a technical issue on the ProdPilot platform. " +
        "Everything is back to normal and you can resume your activity.\n\n" +
        "If you still experience any difficulty, please reply to this email and we will assist you.\n\n" +
        "Thank you for your patience,\nThe ProdLink team",
    },
    update: {
      label: 'Platform update',
      subject: 'ProdPilot — New update available',
      message:
        "Hello,\n\nA new version of the ProdPilot platform has just been deployed. " +
        "It brings several improvements designed to streamline your day-to-day work on the tool.\n\n" +
        "Sign in now to discover the latest features.\n\n" +
        "The ProdLink team",
    },
    announce: {
      label: 'Important announcement',
      subject: 'ProdPilot — Important announcement',
      message:
        "Hello,\n\nWe would like to inform you of an important update regarding your collaboration " +
        "with ProdLink.\n\n[Specify your announcement here]\n\nThe ProdLink team",
    },
  },
};

const TEMPLATE_KEYS = ['custom', 'bugfix', 'update', 'announce'];

export default function NotifySubcontractorsDialog({ open, onOpenChange, subs }) {
  const [lang, setLang] = useState('fr'); // 'fr' | 'en'
  const [mode, setMode] = useState('selection'); // 'selection' | 'all'
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [onlyWithNda, setOnlyWithNda] = useState(false);

  const [templateKey, setTemplateKey] = useState('custom');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const applyTemplate = (key, langOverride) => {
    setTemplateKey(key);
    const useLang = langOverride || lang;
    if (key !== 'custom') {
      const tpl = TEMPLATES[useLang][key];
      setSubject(tpl.subject);
      setMessage(tpl.message);
    }
  };

  const switchLang = (newLang) => {
    setLang(newLang);
    // Re-apply current template in the new language so subject/body stay in sync
    if (templateKey !== 'custom') {
      const tpl = TEMPLATES[newLang][templateKey];
      setSubject(tpl.subject);
      setMessage(tpl.message);
    }
  };

  // Filtered list shown in selection panel
  const filteredSubs = useMemo(() => {
    return (subs || []).filter((s) => {
      if (onlyActive && s.account_active === false) return false;
      if (onlyWithNda && !s.nda_signed) return false;
      if (filterCategory.trim()) {
        const cat = filterCategory.trim().toLowerCase();
        if (!(s.specialty || '').toLowerCase().includes(cat)) return false;
      }
      if (filterCountry.trim()) {
        const co = filterCountry.trim().toLowerCase();
        if (!(s.country || '').toLowerCase().includes(co)) return false;
      }
      return true;
    });
  }, [subs, onlyActive, onlyWithNda, filterCategory, filterCountry]);

  // How many will actually receive an email (have an address)
  const recipientsPreview = useMemo(() => {
    const pool = mode === 'all' ? filteredSubs : (subs || []).filter((s) => selectedIds.includes(s.id));
    const withEmail = pool.filter((s) => (s.email || '').trim() && (s.email || '').includes('@'));
    return {
      total: pool.length,
      withEmail: withEmail.length,
      withoutEmail: pool.length - withEmail.length,
    };
  }, [mode, filteredSubs, subs, selectedIds]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllVisible = () => {
    setSelectedIds(filteredSubs.map((s) => s.id));
  };
  const clearSelection = () => setSelectedIds([]);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Sujet requis');
      return;
    }
    if (!message.trim()) {
      toast.error('Message requis');
      return;
    }
    if (mode === 'selection' && selectedIds.length === 0) {
      toast.error('Sélectionnez au moins un sous-traitant');
      return;
    }
    if (recipientsPreview.withEmail === 0) {
      toast.error('Aucun destinataire avec un email valide');
      return;
    }
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        message,
        lang,
        only_active: onlyActive,
        only_with_nda: onlyWithNda,
      };
      if (mode === 'all') {
        payload.all_subs = true;
        if (filterCategory.trim()) payload.category = filterCategory.trim();
        if (filterCountry.trim()) payload.country = filterCountry.trim();
      } else {
        payload.subcontractor_ids = selectedIds;
      }
      const { data } = await api.post('/subcontractors/notify', payload);
      toast.success(`Email envoyé à ${data.sent_count} sous-traitant(s)`);
      if (data.skipped_no_email_count > 0) {
        toast.warning(`${data.skipped_no_email_count} sous-traitant(s) ignoré(s) (email manquant)`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="notify-subs-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-xl font-bold flex items-center gap-2"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            <Mail className="w-5 h-5 text-amber-400" />
            Notifier les sous-traitants
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Envoyez un email à plusieurs sous-traitants. Ils ne se voient pas entre eux (BCC).
            L&apos;adresse <span className="text-amber-400">prodlinkfr@gmail.com</span> est automatiquement en copie cachée.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Mode toggle */}
          <div className="flex items-center gap-2 border border-zinc-800 rounded-sm p-1 bg-zinc-950 w-fit">
            <button
              data-testid="notify-mode-selection"
              onClick={() => setMode('selection')}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${
                mode === 'selection' ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Users className="w-3.5 h-3.5 inline mr-1.5" />
              Sélection manuelle
            </button>
            <button
              data-testid="notify-mode-all"
              onClick={() => setMode('all')}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${
                mode === 'all' ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Tous (avec filtres)
            </button>
          </div>

          {/* Filters */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Filtres</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-400">Catégorie / Spécialité</Label>
                <Input
                  data-testid="notify-filter-category"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  placeholder="Ex. usinage, peinture..."
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-400">Pays</Label>
                <Input
                  data-testid="notify-filter-country"
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                  placeholder="Ex. France, Maroc, Pologne..."
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <Checkbox
                  data-testid="notify-filter-active"
                  checked={onlyActive}
                  onCheckedChange={(v) => setOnlyActive(!!v)}
                />
                Comptes actifs uniquement
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <Checkbox
                  data-testid="notify-filter-nda"
                  checked={onlyWithNda}
                  onCheckedChange={(v) => setOnlyWithNda(!!v)}
                />
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                NDA signé uniquement
              </label>
            </div>
          </div>

          {/* Selection list (only in selection mode) */}
          {mode === 'selection' && (
            <div className="border border-zinc-800 rounded-sm">
              <div className="flex items-center justify-between p-2.5 border-b border-zinc-800 bg-zinc-950">
                <div className="text-xs text-zinc-400">
                  {filteredSubs.length} sous-traitant(s) — {selectedIds.length} sélectionné(s)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="notify-select-all-visible"
                    onClick={selectAllVisible}
                    className="text-xs text-amber-400 hover:text-amber-300"
                  >
                    Tout sélectionner
                  </button>
                  <span className="text-zinc-700">|</span>
                  <button
                    data-testid="notify-clear-selection"
                    onClick={clearSelection}
                    className="text-xs text-zinc-500 hover:text-zinc-300"
                  >
                    Effacer
                  </button>
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-zinc-800/60">
                {filteredSubs.length === 0 ? (
                  <p className="text-center text-xs text-zinc-600 py-6">Aucun sous-traitant ne correspond aux filtres</p>
                ) : (
                  filteredSubs.map((s) => {
                    const hasEmail = (s.email || '').includes('@');
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 p-2.5 hover:bg-zinc-800/30 cursor-pointer"
                        data-testid={`notify-row-${s.id}`}
                      >
                        <Checkbox
                          checked={selectedIds.includes(s.id)}
                          onCheckedChange={() => toggleSelect(s.id)}
                          data-testid={`notify-check-${s.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-zinc-200 truncate">{s.company_name}</p>
                            {s.nda_signed && (
                              <ShieldCheck className="w-3 h-3 text-emerald-400" title="NDA signé" />
                            )}
                          </div>
                          <p className={`text-[11px] truncate ${hasEmail ? 'text-zinc-500' : 'text-rose-400'}`}>
                            {hasEmail ? s.email : 'Email manquant — sera ignoré'}
                          </p>
                        </div>
                        {s.specialty && (
                          <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-sm">
                            {s.specialty}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Language + Template */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Langue & Modèle</Label>
              <div className="flex items-center gap-1 border border-zinc-800 rounded-sm p-0.5 bg-zinc-950">
                <button
                  data-testid="notify-lang-fr"
                  onClick={() => switchLang('fr')}
                  className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                    lang === 'fr' ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  🇫🇷 FR
                </button>
                <button
                  data-testid="notify-lang-en"
                  onClick={() => switchLang('en')}
                  className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                    lang === 'en' ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  🇬🇧 EN
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATE_KEYS.map((key) => {
                const tpl = TEMPLATES[lang][key];
                return (
                  <button
                    key={key}
                    data-testid={`notify-tpl-${key}`}
                    onClick={() => applyTemplate(key)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                      templateKey === key
                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-300'
                        : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    {tpl.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject + Body */}
          <div className="space-y-2">
            <Label className="text-xs text-zinc-400">Sujet</Label>
            <Input
              data-testid="notify-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Sujet du mail"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-zinc-400">Message</Label>
            <Textarea
              data-testid="notify-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              placeholder="Votre message..."
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm font-mono text-sm"
            />
          </div>

          {/* Recipients summary */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 flex items-center justify-between">
            <div className="text-xs">
              <span className="text-zinc-500">Destinataires : </span>
              <span className="text-emerald-400 font-medium" data-testid="notify-count">
                {recipientsPreview.withEmail}
              </span>
              <span className="text-zinc-500"> recevront l&apos;email</span>
              {recipientsPreview.withoutEmail > 0 && (
                <span className="text-rose-400 ml-2">
                  · {recipientsPreview.withoutEmail} ignoré(s) (sans email)
                </span>
              )}
            </div>
            {recipientsPreview.withoutEmail > 0 && (
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm"
          >
            Annuler
          </Button>
          <Button
            data-testid="notify-send-btn"
            onClick={handleSend}
            disabled={sending || recipientsPreview.withEmail === 0}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Envoi...' : `Envoyer (${recipientsPreview.withEmail})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
